import type { JsonValue } from '@backstage/types';

/**
 * A prefilled scaffolder run, handed to the browser by something outside it —
 * an AI skill driving the MCP read/render tools, say, which can dry-run a
 * template but cannot execute one: publishing needs the browser's GitHub OAuth
 * flow, and the MCP servers are filtered read-only.
 */
export interface ScaffolderDraft {
  /** e.g. `template:default/new-internal-app`. */
  templateRef: string;
  /** Initial form values; the shape is the template's own parameter schema. */
  values: Record<string, JsonValue>;
}

/** Fragment key holding the encoded draft, e.g. `/create/draft#v=eyJ0…`. */
const DRAFT_FRAGMENT_KEY = 'v';

/**
 * First two bytes of a gzip member (RFC 1952 §2.3.1), used to tell the two
 * payload formats apart.
 *
 * The check is unambiguous rather than a guess: an uncompressed payload is
 * `encodeURIComponent` output, and every draft is a JSON *object*, so it
 * always begins `%7B` — byte `0x25`. No percent-encoded string can start
 * `0x1f 0x8b`, and no gzip member can start `0x25`.
 */
const GZIP_MAGIC = [0x1f, 0x8b];

/**
 * Drafts travel in the URL *fragment*, gzipped and base64url-encoded.
 *
 * The fragment rather than a query parameter, because the fragment is never
 * sent to the server. A query parameter has to fit inside Node's 16KB
 * `maxHeaderSize` (and nginx's 8KB header buffer), shared with cookies — and
 * these payloads have no useful upper bound, since new-internal-app's
 * `configFile.content` is a textarea with no `maxLength`. It would also land
 * in every access log along the way.
 *
 * Gzip roughly halves a prose-heavy draft and shrinks a config-file-heavy one
 * by an order of magnitude. The point is not sharing: a draft is normally
 * handed straight from a local agent to the same person's browser, so what the
 * link has to survive is a terminal and the address bar, both of which get
 * unpleasant with the six-figure URLs a filled-in config file used to produce.
 * It is not a correctness requirement — the fragment has no size limit — so
 * `decodeDraft` still accepts the uncompressed format it used to emit. Draft
 * links are deliberately stateless and therefore never expire, so that older
 * format is a permanent second input, not a migration to finish.
 *
 * Nothing is stored server-side: no draft table, no TTL to tune, nothing to
 * clean up. The cost is that a draft link never expires, which is acceptable
 * because it cannot act — it only prefills a form. The run still needs a human
 * to connect GitHub and press Create.
 */
export async function encodeDraft(draft: ScaffolderDraft): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(draft));
  return bytesToBase64Url(await through(json, new CompressionStream('gzip')));
}

/**
 * The app itself never calls `encodeDraft` — it only ever reads a link someone
 * else built, so `decodeDraft` below is the half that ships. `encodeDraft` is
 * kept here anyway, next to its counterpart, because it is the reference
 * implementation of the wire format: the producers are outside this repo (an
 * MCP client, a skill) and have to match it byte for byte. Keeping the pair in
 * one file is also what lets the tests prove the two halves agree.
 */

/**
 * base64url via `btoa`/`atob` rather than `Buffer`.
 *
 * `Buffer` does exist in the browser bundle — webpack shims it — but the shim
 * is an old `buffer` package that does not know the `base64url` encoding, so
 * `Buffer.from(s, 'base64url')` throws "Unknown encoding: base64url" in the
 * browser while working perfectly in Node. Unit tests run in Node and would
 * not have caught it.
 *
 * base64url (not base64) so the result can sit in a URL untouched: `+` and `/`
 * would otherwise need escaping, and `=` padding is noise.
 */
function bytesToBase64Url(bytes: Uint8Array): string {
  // Chunked rather than `String.fromCharCode(...bytes)`: spreading a large
  // payload as arguments overflows the call stack.
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** base64url → the latin1 string `atob` produces, one char per byte. */
function base64UrlToBinary(encoded: string): string {
  return atob(encoded.replace(/-/g, '+').replace(/_/g, '/'));
}

function binaryToBytes(binary: string): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Push `bytes` through a compression stream and collect the result.
 *
 * The write is started but not awaited before reading begins: a
 * `CompressionStream` applies backpressure once its internal queue fills, so
 * awaiting the write first would deadlock on any payload large enough to
 * matter — exactly the ones this exists for.
 */
async function through(
  // `Uint8Array<ArrayBuffer>`, not a bare `Uint8Array`: the latter widens to
  // `ArrayBufferLike`, which admits `SharedArrayBuffer` and so is not a
  // `BufferSource` the stream will accept.
  bytes: Uint8Array<ArrayBuffer>,
  // Structural rather than `CompressionStream | DecompressionStream`: the two
  // are distinct nominal types in lib.dom, and the write side is typed
  // `BufferSource`, not `Uint8Array`.
  stream: {
    readable: ReadableStream<Uint8Array>;
    writable: WritableStream<BufferSource>;
  },
): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  const written = (async () => {
    await writer.write(bytes);
    await writer.close();
  })().catch(() => {
    // A corrupt payload makes the stream error, which rejects *both* ends. The
    // read side below is the one that reports it, so this rejection is
    // deliberately swallowed — left floating it takes down the whole process
    // as an unhandled rejection instead of surfacing as a bad draft link.
  });

  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    length += value.length;
  }
  await written;

  const out = new Uint8Array(length);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}

/**
 * Decode a draft, or throw with a reason a user can act on.
 *
 * Deliberately strict. A half-understood draft would prefill a form with some
 * fields missing and no indication which, which is worse than refusing: the
 * user would submit it believing it matched what they were shown.
 */
export async function decodeDraft(encoded: string): Promise<ScaffolderDraft> {
  let parsed: unknown;
  try {
    const binary = base64UrlToBinary(encoded);
    const gzipped = GZIP_MAGIC.every(
      (byte, i) => binary.charCodeAt(i) === byte,
    );
    const json = gzipped
      ? new TextDecoder().decode(
          await through(binaryToBytes(binary), new DecompressionStream('gzip')),
        )
      : // The pre-gzip format: `encodeURIComponent` applied before base64, so
        // that `btoa` never saw a code unit above 255. Still emitted by any
        // producer that has not been updated, and by every link already out
        // there.
        decodeURIComponent(binary);
    parsed = JSON.parse(json);
  } catch (e) {
    throw new Error(
      `This draft link is corrupt or truncated — ask for a fresh one. (${
        e instanceof Error ? e.message : String(e)
      })`,
    );
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('This draft link does not contain a scaffolder draft.');
  }
  const { templateRef, values } = parsed as Partial<ScaffolderDraft>;
  if (typeof templateRef !== 'string' || !templateRef) {
    throw new Error('This draft link is missing the template it applies to.');
  }
  if (typeof values !== 'object' || values === null || Array.isArray(values)) {
    throw new Error('This draft link is missing its form values.');
  }
  return { templateRef, values };
}

/**
 * `template:default/new-internal-app` → `{ namespace, name }`.
 *
 * Only Template refs are accepted: this ref decides what the page renders, and
 * it arrives from a URL, so a crafted link should not be able to point the
 * form at some other kind of entity.
 */
export function parseTemplateRef(templateRef: string): {
  namespace: string;
  name: string;
} {
  const match = /^template:([^/]+)\/(.+)$/.exec(templateRef);
  if (!match) {
    throw new Error(
      `"${templateRef}" is not a template reference — expected template:<namespace>/<name>.`,
    );
  }
  return { namespace: match[1], name: match[2] };
}

/** Read the encoded draft out of a location hash, if there is one. */
export function draftFromHash(hash: string): string | undefined {
  return (
    new URLSearchParams(hash.replace(/^#/, '')).get(DRAFT_FRAGMENT_KEY) ??
    undefined
  );
}
